import { request, Paginated } from "utils/api";
import { ReducedUserProfileWithEmail } from "api/user";
import { Membership } from "api/memberships";

// member from GET /admin/members
export interface Member extends ReducedUserProfileWithEmail {
    isAlias: boolean,
    isBlockedForBookings: boolean,
    mainAccount?: ReducedUserProfileWithEmail,
    membershipId: string,
    userRole: Membership["userRole"],
    clubName: string;
    monthlyCompanyMemberBudgetCents?: number;
    site?: string;
}

// name change from GET /admin/members/name-changes
export interface NameChange {
    createdAt: string,
    newFullName: string,
    originalFullName: string,
}

// membership request from GET /admin/memberships/requests
export interface MembershipRequest {
    createdAt: string,
    id: string,
    user: ReducedUserProfileWithEmail,
}

// fetch members (GET /admin/members)
export const getMembers = (data: {
    clubId?: string,
    limit?: number,
    offset?: number,
    query?: string,
    sortKey?: "firstName" | "lastName",
    sortOrder?: "ASC" | "DESC",
}) => request<Paginated<Member>>({
    data,
    endpoint: "/admin/members",
    method: "get",
});

// edit members (PATCH /admin/members)
export const patchMembers = (data: {
    isBlockedForBookings: boolean,
    membershipIds: string[],
    userRole: Membership["userRole"],
    site?: string,
}) => request<Member[]>({
    data,
    endpoint: "/admin/members",
    method: "patch",
});

// delete members (PATCH /admin/members)
export const deleteMembers = (data: {
    membershipIds: string[],
}) => request<{}>({
    data,
    endpoint: "/admin/members",
    method: "delete",
});

// bulk edit members (PATCH /admin/members/bulk)
export const patchMembersBulk = (data: {
    clubId?: string,
    isBlockedForBookings: boolean,
    query?: string,
    userRole: Membership["userRole"],
    site?: string;
}) => request<Member[]>({
    data,
    endpoint: "/admin/members/bulk",
    method: "patch",
});

// bulk delete members (DELETE /admin/members/bulk)
export const deleteMembersBulk = (data: {
    clubId?: string,
    query?: string,
}) => request<{}>({
    data,
    endpoint: "/admin/members/bulk",
    method: "delete",
});

// fetch name changes (GET /admin/members/name-changes)
export const getMembersNameChanges = (data: {
    clubId?: string,
    limit?: number,
    offset?: number,
}) => request<Paginated<NameChange>>({
    data,
    endpoint: "/admin/members/name-changes",
    method: "get",
});

// fetch membership requests (GET /admin/memberships/requests)
export const getMembershipRequests = (data: {
    clubId?: string,
    limit?: number,
    offset?: number,
    query?: string,
    sortKey?: "firstName" | "lastName" | "createdAt",
    sortOrder?: "ASC" | "DESC",
}) => request<Paginated<MembershipRequest>>({
    data,
    endpoint: "/admin/memberships/requests",
    method: "get",
});

// respond membership requests (PUT /admin/memberships/requests)
export const respondMembershipRequests = (data: {
    approve: boolean,
    requestIds: string[],
}) => request<{}>({
    data,
    endpoint: "/admin/memberships/requests",
    method: "put",
});

// bulk respond membership requests (PUT /admin/memberships/requests)
export const respondMembershipRequestsBulk = (data: {
    approve: boolean,
    clubId?: string,
    query?: string,
}) => request<{}>({
    data,
    endpoint: "/admin/memberships/requests/bulk",
    method: "put",
});

// export members (POST /admin/members/exports)
export const postMembersExport = (data: {
    clubId?: string,
}) => request<{ url: string }>({
    data,
    endpoint: "/admin/members/exports",
    method: "post",
});

export const getAdminMemberCount = (data: {
    clubId?: string,
    bookingId?: string,
    clubSite?: string,
}) => request<{ totalCount: number, invitedCount: number }>({
    data,
    endpoint: "/admin/members/count",
    method: "get",
});
